const aiTools = {
  title: "AI Tools & Applications",
  subtitle: "A practical guide to AI tools for coding, writing, research, and daily work — what actually helps and what is hype.",
  sections: [
    {
      id: "coding-tools",
      title: "AI for Coding",
      content: [
        "AI coding tools have become genuinely useful — not as replacements for developers, but as accelerators that handle boilerplate, catch bugs, and help you think through problems faster [1].",
        {
          type: "bullets",
          items: [
            "GitHub Copilot: inline code suggestions as you type — best for boilerplate, repetitive patterns, and completing functions from a comment",
            "Claude Code: agentic coding in the terminal — can read your codebase, run commands, write and edit files, and execute multi-step tasks",
            "Cursor / Windsurf: AI-native code editors that integrate chat, inline edits, and codebase understanding into the IDE",
            "ChatGPT / Claude chat: useful for debugging, explaining unfamiliar code, writing tests, and exploring architectural ideas",
            "Codex CLI / Aider: command-line tools that can make changes across multiple files with a single natural language instruction"
          ]
        },
        "The key to using coding AI well is treating it as a junior developer who is very fast but needs clear instructions and code review. Never blindly accept generated code — read it, test it, understand it."
      ]
    },
    {
      id: "writing-tools",
      title: "AI for Writing",
      content: [
        "AI is remarkably good at writing tasks — not because it writes better than humans, but because it removes the friction of the blank page and handles mechanical tasks like summarizing, reformatting, and translating [2].",
        {
          type: "bullets",
          items: [
            "Drafting: give it an outline and context, and it produces a reasonable first draft you can refine",
            "Editing: paste your text and ask it to improve clarity, fix grammar, or adjust tone — faster than self-editing",
            "Summarizing: condense long documents, meeting transcripts, or research papers into key points",
            "Translating: not just languages but registers — turning technical jargon into plain English, or formal to casual",
            "Email and communication: draft professional emails, responses, or announcements from bullet points"
          ]
        },
        "The risk with AI writing is homogeneity. AI prose has a recognizable cadence — overly polished, hedging, generic. Use it for structure and mechanics, but inject your own voice and specificity."
      ]
    },
    {
      id: "research-tools",
      title: "AI for Research and Learning",
      content: [
        "AI tools can accelerate research by helping you find, synthesize, and understand information faster — but they should complement, not replace, primary sources.",
        {
          type: "bullets",
          items: [
            "Perplexity: AI search engine that cites its sources — good for factual research with verification",
            "NotebookLM (Google): upload documents and have conversations with them — excellent for studying papers or reports",
            "Claude with long context: can process entire books or codebases and answer questions about them with high accuracy",
            "Elicit / Semantic Scholar: AI-powered academic paper search and analysis",
            "ChatGPT with browsing: can search the web and synthesize current information, though always verify"
          ]
        },
        "Always verify AI-generated research against primary sources. Use AI to find and organize information, but form your own conclusions."
      ]
    },
    {
      id: "image-tools",
      title: "AI for Images and Design",
      content: [
        "Image generation and editing AI has progressed rapidly, making visual content creation accessible to non-designers [3].",
        {
          type: "bullets",
          items: [
            "Midjourney: high-quality image generation — excels at artistic, photorealistic, and conceptual imagery",
            "DALL-E (OpenAI): integrated into ChatGPT — good for quick illustrations, diagrams, and concept art",
            "Stable Diffusion: open-source image generation — can be run locally for privacy and customization",
            "Adobe Firefly: commercially safe AI generation integrated into Photoshop and other Adobe tools",
            "Remove.bg / Cleanup.pictures: AI-powered background removal and object removal from photos"
          ]
        }
      ]
    },
    {
      id: "productivity-tools",
      title: "AI for Daily Productivity",
      content: [
        "The most impactful AI use cases are often the most mundane — automating small, repetitive tasks that add up to hours of saved time each week.",
        {
          type: "bullets",
          items: [
            "Meeting transcription: Otter.ai, Fireflies, or built-in Zoom/Teams AI — automatic notes and action items from meetings",
            "Calendar management: AI assistants that schedule meetings, find free time, and handle rescheduling",
            "Data extraction: pull structured data from receipts, invoices, screenshots, or PDFs",
            "Workflow automation: Zapier AI, Make, or custom LLM calls to connect apps and automate repetitive processes",
            "Voice interaction: dictate notes, emails, or code using AI-powered speech-to-text (Whisper, built-in OS features)"
          ]
        }
      ]
    },
    {
      id: "choosing-tools",
      title: "How to Choose the Right AI Tool",
      content: [
        "With hundreds of AI tools launching every week, it is easy to get overwhelmed. Here is a framework for deciding what is worth your time.",
        {
          type: "bullets",
          items: [
            "Start with the task, not the tool: identify your actual friction points before looking for AI solutions",
            "Try the free tier first: most AI tools offer enough free usage to evaluate whether they fit your workflow",
            "Favour integrated tools: an AI feature inside your existing editor or browser is worth more than a standalone app you will forget about",
            "Consider data privacy: understand where your data goes — especially for proprietary code or sensitive documents",
            "Evaluate honestly: after a week, ask 'did this actually save me time, or did I just enjoy playing with it?'",
            "Keep it simple: using one AI tool well beats using five AI tools poorly"
          ]
        }
      ]
    }
  ],
  dosAndDonts: [
    {
      category: "Using AI Effectively",
      items: [
        { do: "Review and test all AI-generated code before using it", dont: "Blindly copy-paste AI output into production" },
        { do: "Provide context and examples in your prompts", dont: "Give vague, one-line prompts and expect perfect results" },
        { do: "Use AI to accelerate tasks you already understand", dont: "Use AI to do tasks you cannot evaluate or verify" },
        { do: "Iterate on prompts — refine based on output", dont: "Give up after one bad response" },
        { do: "Verify facts and citations from AI research", dont: "Treat AI responses as authoritative sources" },
        { do: "Learn the fundamentals of AI to use it better", dont: "Treat AI as pure magic — understanding helps" }
      ]
    }
  ],
  references: [
    { id: 1, text: "Peng, S. et al. — The Impact of AI on Developer Productivity: Evidence from GitHub Copilot, 2023", url: "https://arxiv.org/abs/2302.06590" },
    { id: 2, text: "Noy, S. & Zhang, W. — Experimental Evidence on the Productivity Effects of Generative AI, Science 2023" },
    { id: 3, text: "Ramesh, A. et al. — Hierarchical Text-Conditional Image Generation with CLIP Latents (DALL-E 2), 2022", url: "https://arxiv.org/abs/2204.06125" }
  ]
};

export default aiTools;
